/**
 * Der Abgleich (T-04): Server-Aenderungen holen und zusammenfuehren, dann
 * eigene offene Aenderungen hochladen. Laeuft vollstaendig im Browser; der
 * Server sieht nur Geheimtext (ADR 0006).
 *
 * Ablauf:
 *   1. Ziehen: alles seit der letzten Laufnummer, seitenweise. Jede Zeile wird
 *      entschluesselt und je Feld mit dem lokalen Stand zusammengefuehrt.
 *   2. Schieben: offene Eintraege in Paketen. Lehnt der Server eine Zeile ab
 *      (ein anderes Geraet war schneller), kommt sie zurueck, wird wie in 1.
 *      zusammengefuehrt und im naechsten Durchgang erneut gesendet.
 *      Papierkorb-Zeilen (F-45) gehen erst hoch, wenn keine Datenzeile mehr
 *      offen ist - siehe `schieben`.
 *
 * Anhaenge: Die Datei selbst liegt verschluesselt in R2. Sie wird vor ihrer
 * Zeile hochgeladen und vor dem Uebernehmen heruntergeladen - ein Eintrag
 * ohne Datei kommt so nie in die lokale Datenbank.
 */
import type { OlePrivatDB } from '@/db/schema';
import { PAPIERKORB_SYNC_NAMES, SYNC_TABLE_NAMES } from '@/db/tabellen';
import { alsUebernahme } from '@/db/sync/erfassung';
import { eintragTitel, syncSchluessel, type SyncFeldStand, type SyncKonflikt } from '@/domain/sync/typen';
import { gleich, gleicheUhren, lebendig, zusammenfuehren, type Daten, type Fassung } from '@/domain/sync/zusammenfuehren';
import type { Uhr, Uhrwerk } from '@/domain/sync/uhr';
import { entschluesseln, jsonEntschluesseln, jsonVerschluesseln, verschluesseln } from '@/lib/sync/krypto';
import { SyncFehler, type SendeZeile, type ServerZeile, type SyncApi } from '@/lib/sync/api';

export const META = {
  konfig: 'sync.konfig',
  schluessel: 'sync.schluessel',
  cursor: 'sync.cursor',
  uhr: 'sync.uhr',
  zuletzt: 'sync.zuletzt',
} as const;

export interface SyncKontext {
  db: OlePrivatDB;
  api: SyncApi;
  schluessel: CryptoKey;
  uhr: Uhrwerk;
  jetzt?: () => Date;
}

/** Inhalt eines Server-Blobs. `v` = Datenbankversion des Absenders. */
interface Inhalt { v: number; d: Daten | null; f: Record<string, Uhr>; x?: Uhr }

const PAKET = 50;
const DURCHGAENGE = 5;
const TABELLEN = new Set<string>(SYNC_TABLE_NAMES);
const PAPIERKORB = new Set<string>(PAPIERKORB_SYNC_NAMES);
const ANHANG = 'attachments';

const metaLesen = async <T>(db: OlePrivatDB, key: string, fallback: T): Promise<T> => ((await db.meta.get(key))?.value as T | undefined) ?? fallback;
const metaSchreiben = (db: OlePrivatDB, key: string, value: unknown) => db.meta.put({ key, value });

interface Eingang { t: string; id: string; seq: number; fern: Fassung }

async function oeffnen(k: SyncKontext, z: ServerZeile): Promise<Eingang> {
  const b = await jsonEntschluesseln<Inhalt>(k.schluessel, z.blob, syncSchluessel(z.t, z.id)).catch(() => {
    throw new SyncFehler('passwort', 'Eintrag lässt sich nicht entschlüsseln - stimmt das Sync-Passwort?');
  });
  if (b.v > k.db.verno) throw new SyncFehler('version', 'Das andere Gerät nutzt eine neuere App-Version - bitte diese Seite neu laden');
  for (const u of Object.values(b.f)) k.uhr.sehen(u);
  if (b.x) k.uhr.sehen(b.x);
  return { t: z.t, id: z.id, seq: z.seq, fern: { d: b.d, f: b.f, ...(b.x ? { x: b.x } : {}) } };
}

/** Fehlende Anhangsdateien holen, bevor die Zeilen geschrieben werden. */
async function dateienHolen(k: SyncKontext, eingaenge: Eingang[]): Promise<void> {
  for (const e of eingaenge) {
    if (e.t !== ANHANG || !e.fern.d || !lebendig(e.fern.f, e.fern.x)) continue;
    const lokal = (await k.db.attachments.get(e.id)) as Daten | undefined;
    if (lokal?.bytes) { e.fern.d = { ...e.fern.d, bytes: lokal.bytes }; continue; }
    const geheim = await k.api.anhangHolen(e.id);
    if (!geheim) throw new SyncFehler('server', `Datei zum Anhang „${eintragTitel(e.fern.d)}“ fehlt auf dem Server`);
    e.fern.d = { ...e.fern.d, bytes: await entschluesseln(k.schluessel, geheim, `anhang/${e.id}`) };
  }
}

/** Server-Fassungen mit dem lokalen Stand zusammenfuehren und speichern. */
export async function uebernehmen(k: SyncKontext, eingaenge: Eingang[]): Promise<number> {
  const relevant = eingaenge.filter((e) => TABELLEN.has(e.t));
  if (!relevant.length) return 0;
  await dateienHolen(k, relevant);
  const zeit = (k.jetzt?.() ?? new Date()).toISOString();
  const { db } = k;
  let geaendert = 0;
  const tabellen = [...new Set(relevant.map((e) => e.t))].map((t) => db.table(t));

  await db.transaction('rw', [...tabellen, db.syncFelder, db.syncKonflikte], async (tx) => {
    alsUebernahme(tx);
    for (const e of relevant) {
      const schluessel = syncSchluessel(e.t, e.id);
      const stand = await db.syncFelder.get(schluessel);
      if (stand && stand.seq >= e.seq) continue;
      const tabelle = db.table(e.t);
      const lokalD = ((await tabelle.get(e.id)) as Daten | undefined) ?? null;
      const lokal: Fassung = { d: lokalD, f: stand?.f ?? {}, ...(stand?.x ? { x: stand.x } : {}) };
      const { fassung, konflikte } = zusammenfuehren(lokal, { f: stand?.bf ?? {}, ...(stand?.bx ? { x: stand.bx } : {}) }, e.fern);

      if (fassung.d && !gleich(lokalD, fassung.d)) { await tabelle.put(fassung.d); geaendert++; }
      if (!fassung.d && lokalD) { await tabelle.delete(e.id); geaendert++; }

      const neu: SyncFeldStand = {
        schluessel, tabelle: e.t, id: e.id, f: fassung.f, bf: e.fern.f, seq: e.seq,
        offen: gleicheUhren(fassung, e.fern) ? 0 : 1,
      };
      if (fassung.x) neu.x = fassung.x;
      if (e.fern.x) neu.bx = e.fern.x;
      // Nur ein lebender Anhang vom Server hat dort auch seine Datei. Ein
      // geloeschter hat sie nicht mehr - wird er wiederhergestellt (F-45),
      // muss sie neu hoch.
      if (e.t === ANHANG && fassung.d && e.fern.d) neu.datei = 1;
      await db.syncFelder.put(neu);

      const titel = eintragTitel(lokalD ?? e.fern.d);
      for (const kf of konflikte) {
        const eintrag: SyncKonflikt = { id: crypto.randomUUID(), zeit, tabelle: e.t, eintragId: e.id, titel, art: kf.art };
        if (kf.feld) eintrag.feld = kf.feld;
        if (kf.verloren !== undefined) eintrag.verloren = kf.verloren;
        if (kf.gewonnen !== undefined) eintrag.gewonnen = kf.gewonnen;
        await db.syncKonflikte.add(eintrag);
      }
    }
  });
  return geaendert;
}

async function ziehen(k: SyncKontext): Promise<number> {
  let seit = await metaLesen(k.db, META.cursor, 0);
  let geaendert = 0;
  for (;;) {
    const { zeilen, mehr } = await k.api.aenderungen(seit);
    if (!zeilen.length) break;
    geaendert += await uebernehmen(k, await Promise.all(zeilen.map((z) => oeffnen(k, z))));
    seit = zeilen[zeilen.length - 1]!.seq;
    await metaSchreiben(k.db, META.cursor, seit);
    if (!mehr) break;
  }
  return geaendert;
}

/** Die genannten offenen Eintraege in Paketen senden. Gibt die Zahl der angenommenen zurueck. */
async function senden(k: SyncKontext, offen: readonly SyncFeldStand[]): Promise<number> {
  const { db } = k;
  let gesendet = 0;
  for (let i = 0; i < offen.length; i += PAKET) {
    const paket = offen.slice(i, i + PAKET);
    const zeilen: SendeZeile[] = [];
    const anhaengeHoch: string[] = [];
    for (const s of paket) {
      const d = ((await db.table(s.tabelle).get(s.id)) as Daten | undefined) ?? null;
      const da = d !== null && lebendig(s.f, s.x);
      let inhalt = da ? d : null;
      if (s.tabelle === ANHANG && inhalt) {
        const { bytes, ...rest } = inhalt;
        if (!s.datei && bytes instanceof Uint8Array) {
          await k.api.anhangHoch(s.id, await verschluesseln(k.schluessel, bytes as Uint8Array<ArrayBuffer>, `anhang/${s.id}`));
          anhaengeHoch.push(s.schluessel);
        }
        inhalt = rest;
      }
      const blob: Inhalt = { v: db.verno, d: inhalt, f: s.f, ...(s.x ? { x: s.x } : {}) };
      zeilen.push({ t: s.tabelle, id: s.id, basis: s.seq, blob: await jsonVerschluesseln(k.schluessel, blob, s.schluessel) });
    }

    const antwort = await k.api.senden(zeilen);
    const nachSchluessel = new Map(paket.map((s) => [s.schluessel, s]));
    const geloeschteAnhaenge: string[] = [];
    await db.transaction('rw', db.syncFelder, async () => {
      for (const ok of antwort.ok) {
        const schluessel = syncSchluessel(ok.t, ok.id);
        const gesendetStand = nachSchluessel.get(schluessel);
        const jetzt = await db.syncFelder.get(schluessel);
        if (!gesendetStand || !jetzt) continue;
        // Waehrend des Sendens erneut bearbeitet? Dann bleibt der Eintrag offen.
        const unveraendert = gleicheUhren(jetzt, gesendetStand);
        const neu: SyncFeldStand = { ...jetzt, seq: ok.seq, bf: gesendetStand.f, offen: unveraendert ? 0 : 1 };
        if (gesendetStand.x) neu.bx = gesendetStand.x; else delete neu.bx;
        if (anhaengeHoch.includes(schluessel)) neu.datei = 1;
        // Geloeschter Anhang: Die Datei wird gleich vom Server entfernt -
        // die Markierung darf eine spaetere Wiederherstellung nicht blockieren.
        const anhangTot = ok.t === ANHANG && !lebendig(gesendetStand.f, gesendetStand.x);
        if (anhangTot) delete neu.datei;
        await db.syncFelder.put(neu);
        if (anhangTot) geloeschteAnhaenge.push(ok.id);
        gesendet++;
      }
    });
    for (const id of geloeschteAnhaenge) await k.api.anhangLoeschen(id).catch(() => undefined);
    if (antwort.abgelehnt.length) await uebernehmen(k, await Promise.all(antwort.abgelehnt.map((z) => oeffnen(k, z))));
  }
  return gesendet;
}

/**
 * Offene Eintraege hochladen, je Durchgang erst alle Datenzeilen. Papierkorb-
 * Zeilen (F-45) gehen erst hoch, wenn KEINE Datenzeile mehr offen ist: Sieht
 * ein anderes Geraet einen Papierkorb-Eintrag, hat es so auch schon alle
 * Loeschaenderungen dazu. Sonst koennte es einen halben Stand wiederherstellen,
 * und der Rest der Loeschung kaeme danach an (Verbindung nach dem ersten Paket
 * abgerissen). Bricht das Senden ab, bleibt der Papierkorb-Eintrag offen.
 */
async function schieben(k: SyncKontext): Promise<number> {
  let gesendet = 0;
  for (let runde = 0; runde < DURCHGAENGE; runde++) {
    const offen = await k.db.syncFelder.where('offen').equals(1).toArray();
    if (!offen.length) return gesendet;
    const daten = offen.filter((s) => !PAPIERKORB.has(s.tabelle));
    gesendet += await senden(k, daten.length ? daten : offen);
  }
  return gesendet;
}

export interface AbgleichErgebnis { geholt: number; gesendet: number; zeit: string }

/** Ein vollstaendiger Abgleich. Wirft `SyncFehler`. */
export async function abgleichen(k: SyncKontext): Promise<AbgleichErgebnis> {
  const geholt = await ziehen(k);
  const gesendet = await schieben(k);
  const zeit = (k.jetzt?.() ?? new Date()).toISOString();
  await metaSchreiben(k.db, META.uhr, k.uhr.stand);
  await metaSchreiben(k.db, META.zuletzt, zeit);
  return { geholt, gesendet, zeit };
}

export const offeneAnzahl = (db: OlePrivatDB) => db.syncFelder.where('offen').equals(1).count();
